import { api } from "@/lib/api";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";

/**
 * Loads what each page needs in the background, once per workspace per
 * visit, so a page already has its content the first time you open it and
 * doesn't flash a loading skeleton or jump.
 *
 * Each entry mirrors the page's own default request and the key it reads
 * from (see useRemembered / lib/cache.js). Requests run one after another so
 * they never compete with the page you're actually looking at, and anything
 * already remembered is skipped. A failure here is silent — the page just
 * loads itself the normal way.
 */
const done = new Set();

export function prefetchWorkspace(tenantId, role) {
  if (!tenantId || done.has(tenantId)) return;
  done.add(tenantId);
  const writer = role === "writer";
  const admin = role === "owner" || role === "admin";
  const tracks = (params) => api.get("/tracks", { params }).then((r) => r.data);
  const get = (url) => api.get(url).then((r) => r.data);
  const jobs = writer
    ? [
        ["my-dashboard", () => get("/my/dashboard")],
        ["my-songs", () => get("/my/songs")],
        ["my-royalties", () => get("/my/royalties")],
        ["payouts-mine", () => get("/payouts/mine")],
      ]
    : [
    ["overview", () => get("/stats")],
    ["catalog", () => tracks({ filter: "all", sort: "release_desc", date_field: "release_date" })],
    ["releases", () => tracks({ sort: "release_desc", date_field: "release_date" }).then((d) => d.tracks)],
    ["writers", () => get("/writers")],
    ["registrations-summary", () => get("/registrations/summary")],
    ["registrations-tracks", () => tracks({ sort: "title", date_field: "release_date" }).then((d) => d.tracks)],
    ["royalties:sources", () => get("/royalties/sources")],
    ...(writer
      ? []
      : [
          ["production", () => tracks({ filter: "unreleased", sort: "updated", date_field: "created_at" }).then((d) => d.tracks)],
          ["albums", () => get("/albums").then(async (d) => {
            // and each album's own page, so opening one from the list or a track paints at once
            for (const a of d.albums || []) {
              const k = cacheKey(`album:${a.album_id}`, tenantId);
              if (cacheGet(k) == null) {
                try {
                  cacheSet(k, await get(`/albums/${a.album_id}`));
                } catch {
                  /* the page loads it itself */
                }
              }
            }
            return d;
          })],
          ["payouts", () => get("/payouts")],
          ["imports", () => get("/imports")],
          ["values", () => get("/values")],
        ]),
    ...(admin ? [["team", () => get("/admin/users")]] : []),
  ];
  const run = async () => {
    for (const [name, load] of jobs) {
      const key = cacheKey(name, tenantId);
      if (cacheGet(key) != null) continue;
      try {
        cacheSet(key, await load());
      } catch {
        /* the page will load it itself */
      }
    }
  };
  setTimeout(run, 600);
}
